import React, { useEffect } from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import moment from "moment";

import ReceiptIcon from "@mui/icons-material/Receipt";
import { useSelector, useDispatch } from "react-redux";
import { setCuota } from "../actions";
import ItemCuotaCard from "~business/Cards/ItemCuotaCard";
import type { Cuota } from "~business/Cards/types";
import {
  CuotaFormulario,
  CuotasFormulario,
  FechaFormulario,
} from "../../types";
import { DatosParaPoliza, InfoEmision } from "~api/types";

const title = "Cuotas";

interface CuotasCardProps {
  isLoading: boolean;
  displayError?: boolean;
  onBlur?: () => void;
}

/**
 * `vigenciaDesde` no lo escribe el selector: el vencimiento de la primera cuota
 * se calcula sobre una fecha indefinida y la card lo recalcula al editarla.
 */
interface SliceCuotas {
  cuotasSelected: CuotasFormulario;
  total: string | number | null;
  cantidadCuotasSelected: number;
  datosPoliza?: DatosParaPoliza | null;
  datosEmision: Partial<InfoEmision>;
  vigenciaDesde?: FechaFormulario;
}

const useReduxSelector = () =>
  useSelector<any, SliceCuotas>((state) => {
    const detalle = state.detalleSolicitudEmisionReducer;
    return {
      cuotasSelected: detalle.cuotasSelected,
      total: detalle.premioTotalSelected.value,
      cantidadCuotasSelected: detalle.cantidadCuotasSelected.value,
      datosPoliza: detalle.detalleSolicitudEmision.datosPoliza,
      datosEmision: detalle.detalleSolicitudEmision.datosEmision,
    };
  });

const CuotasCard: React.FC<CuotasCardProps> = ({
  isLoading,
  displayError,
  onBlur,
}) => {
  const dispatch = useDispatch();

  const {
    total,
    cantidadCuotasSelected,
    cuotasSelected,
    datosPoliza,
    vigenciaDesde,
  } = useReduxSelector();

  useEffect(() => {
    let newImporteCuota = Number(total) / cantidadCuotasSelected;
    let newImportePrimerCuota = newImporteCuota;

    let decimals = newImporteCuota % 1;

    if (decimals !== 0) {
      newImportePrimerCuota =
        newImportePrimerCuota + decimals * (cantidadCuotasSelected - 1);
      newImporteCuota = newImporteCuota - decimals;
    }

    for (let i = 1; i <= cantidadCuotasSelected; i++) {
      const isAlreadyCalculated = datosPoliza && !cuotasSelected[i];

      let fechaVtoCuota = isAlreadyCalculated
        ? moment(datosPoliza.cuotas[i - 1].fechaVencimiento)
        : moment(vigenciaDesde).add(i, "months");

      let importeCuota = isAlreadyCalculated
        ? datosPoliza.cuotas[i - 1].importe
        : i === 1
          ? newImportePrimerCuota
          : newImporteCuota;

      const newCuota: CuotaFormulario = {
        id: i,
        importe: {
          value: importeCuota,
          valid: true,
          focus: false,
        },
        fechaVencimiento: { value: fechaVtoCuota, valid: true, focus: false },
      };

      dispatch(setCuota(newCuota));
    }

    //eslint-disable-next-line
  }, [total]);

  const handleChangeCuota = (cuota: CuotaFormulario) => {
    dispatch(setCuota(cuota));
    updateOthersCuotas(cuota);
  };

  const updateOthersCuotas = (cuota: CuotaFormulario) => {
    const sumCuotasLoaded = Object.keys(cuotasSelected)
      .slice(0, cuota.id)
      .map((key) => parseFloat(String(cuotasSelected[key].importe.value)))
      .reduce((accumulator, value) => accumulator + value);

    const newImporte =
      (Number(total) - sumCuotasLoaded) / (cantidadCuotasSelected - cuota.id);

    Object.keys(cuotasSelected).map((key) => {
      const currentCuota = cuotasSelected[key];

      if (currentCuota.id > cuota.id) {
        const newCuota = {
          ...currentCuota,
          importe: { ...currentCuota.importe, value: newImporte },
        };

        dispatch(setCuota(newCuota));
        return newCuota;
      }

      return currentCuota;
    });

    Object.keys(cuotasSelected).map((key) => {
      const currentCuota = cuotasSelected[key];

      if (cuota.id === 1 && currentCuota.id !== cuota.id) {
        let newFechaVto = moment(cuota.fechaVencimiento.value).add(
          currentCuota.id - cuota.id,
          "months"
        );
        const newCuota = {
          ...currentCuota,
          fechaVencimiento: {
            ...currentCuota.fechaVencimiento,
            value: newFechaVto,
          },
        };

        dispatch(setCuota(newCuota));
        return newCuota;
      }

      return cuota;
    });
  };

  return (
    <DetalleCard isLoading={isLoading} title={title} IconHeader={ReceiptIcon}>
      {Object.keys(cuotasSelected).map((key) => (
        <ItemCuotaCard
          key={key}
          index={Number(key)}
          // La cuota del formulario guarda importes numéricos y fechas moment, y
          // no lleva vigencias: la card sólo lee esos campos en modo readOnly.
          cuota={cuotasSelected[key] as unknown as Cuota}
          onChange={(cuota) =>
            handleChangeCuota(cuota as unknown as CuotaFormulario)
          }
          displayError={displayError}
          onBlur={onBlur}
          focus={
            cuotasSelected[key].importe.focus ||
            cuotasSelected[key].fechaVencimiento.focus
          }
        />
      ))}
    </DetalleCard>
  );
};

export default CuotasCard;
